import {uniqueId, mapValues, find} from 'lodash';
import {useState} from 'react';
import saveArea, {registerSavedArea} from '../utils/gaia/saveArea';
import useIsSaved from './useIsSaved';
import fetchJSON from '../utils/fetchJSON';
import {boldTrackColors} from '../utils/gaia/trackColors';

interface UpdateAction {type: 'update'; area: Area; save: boolean}
interface TakeLocalAction {type: 'takeLocal'; area: RemoteArea}
interface RemoveAction {type: 'remove'; area: Area; save: boolean}

type Action = (
    UpdateAction |
    TakeLocalAction |
    RemoveAction
);

export type AreaDispatcher = (action: Action) => string;

export default function useAreas(
    highlight: MapHighlight
): [{[areaLocalId: string]: Area}, AreaDispatcher, boolean] {

    const [areas, setAreas] = useState<{[areaLocalId: string]: Area}>({});
    const [isSaved, addSavePromise] = useIsSaved();

    function update({area, save}: UpdateAction) {
        setAreas({...areas, [area.properties.areaLocalId]: area});
        if (save) saveArea(area, addSavePromise);

        return area.properties.areaLocalId;
    }

    function takeLocal({area}: TakeLocalAction) {
        const areaLocalId = area.properties.id || uniqueId('area');

        const initialArea: Area = {
            ...area,
            isGeometryLoaded: false,
            properties: {
                areaLocalId,
                isLocallyDeleted: false,
                color: boldTrackColors[0],
                title: '',
                notes: '',

                // We encode "null" as a string in vector tiles
                ...mapValues(area.properties, value =>
                    value === 'null' ? null : value
                ) as RemoteArea['properties'],
            }
        };

        if (!areas[areaLocalId]) {
            setAreas({
                ...areas,
                [areaLocalId]: initialArea,
            });
        }

        const pubLink: string = (
            highlight &&
            highlight.type === 'area' &&
            highlight.pubLink &&
            highlight.id === area.properties.id
        ) ? `?pubLink=${highlight.pubLink}` : '';

        fetchJSON(`/api/objects/area/${area.properties.id}${pubLink}`, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
        }).then(collection => {

            const unmodifiedArea = find(
                collection.features,
                (feature: any) => {
                    return (feature.geometry.type === 'Polygon' ||
                        feature.geometry.type === 'MultiPolygon');
                }
            );

            setAreas(latestAreas => {
                const latestArea: Area = {
                    properties: {
                        areaLocalId,
                        ...initialArea.properties,
                        ...unmodifiedArea.properties,
                    },
                    unmodifiedArea: {
                        ...unmodifiedArea,
                        properties: {
                            ...unmodifiedArea.properties,
                            areaLocalId,
                            isGeometryLoaded: true,
                        },
                    },
                    isGeometryLoaded: true,
                };
                registerSavedArea(latestArea);
                return {
                    ...latestAreas,
                    [areaLocalId]: latestArea
                };
            });
        });

        return areaLocalId;

    }

    function remove(action: RemoveAction) {
        const area = {
            ...action.area,
            properties: {...action.area.properties, isLocallyDeleted: true}
        };
        setAreas({...areas, [area.properties.areaLocalId]: area});
        if (action.save) saveArea(area, addSavePromise);
        return area.properties.areaLocalId;
    }

    function dispatcher(action: Action): string {
        if (action.type === 'update') return update(action);
        else if (action.type === 'takeLocal') return takeLocal(action);
        else if (action.type === 'remove') return  remove(action);
        else throw new Error('Unknown action type');
    }

    return [areas, dispatcher, isSaved];
}
